// Package imports:
import React, { useCallback, useRef, useState } from 'react';
import cx from 'classnames';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { faEye, faEyeSlash, faSearch, faCircleXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Service imports:
import { getCurrencySymbol } from '../../services/utils';
import Cleave from 'cleave.js/react';

type InputType = 'input' | 'numberFormat' | 'textArea' | 'password' | 'ssnFormat' | 'expirationFormat' | 'creditCardFormat' | 'cvcFormat' | 'percentage';
type InputSizes = 'sm' | 'lg';
type TextAlign = 'left' | 'center' | 'right';

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
interface IOwnProps {
    inputType?: InputType,
    inputSize?: InputSizes,
    textAlign?: TextAlign,
    label?: string,
    shortLabel?: string,
    visible?: boolean,
    toggleVisible?(): void,
    currency?: string,
    withSearchGlassIcon?: boolean,
    clearInput?: boolean,
    error?: string;
    fullWidth?: boolean;
    percentageMin?: number;
    percentageMax?: number;
};

type Props = IOwnProps & NumericFormatProps & InputProps & TextAreaProps;

const Input: React.FC<Props> = ({
    // Custom props
    inputType = 'input',
    inputSize = 'lg',
    textAlign = 'left',
    label,
    shortLabel,
    currency,
    error,
    fullWidth = false,
    percentageMin,
    percentageMax = 100,

    // Element props
    id,
    className,
    style,
    onFocus,
    onBlur,
    value,
    onChange,

    visible,
    toggleVisible,
    withSearchGlassIcon,
    clearInput,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const focus = () => setIsFocused(true);
    const blur = () => setIsFocused(false);

    const [showPassword, setShowPassword] = useState(false);
    const togglePassword = () => setShowPassword(!showPassword);

    // Percentage input: store last valid value for revert on blur
    const lastValidPercentage = useRef<string>(value?.toString() ?? '');

    // Update lastValidPercentage ref when value changes from parent with a valid value
    const currentVal = value?.toString() ?? '';
    if (inputType === 'percentage' && currentVal !== '') {
        const num = parseInt(currentVal, 10);
        if (!isNaN(num) && num >= (percentageMin ?? 0) && num <= percentageMax) {
            lastValidPercentage.current = currentVal;
        }
    }

    const fireSyntheticChange = useCallback((newValue: string) => {
        if (onChange) {
            const syntheticEvent = {
                target: { value: newValue },
                currentTarget: { value: newValue }
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
        }
    }, [onChange]);

    const handlePercentageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^0-9]/g, '');
        if (raw === '') {
            fireSyntheticChange('');
            return;
        }
        const num = parseInt(raw, 10);
        if (!isNaN(num) && num <= percentageMax) {
            fireSyntheticChange(raw);
        }
    }, [percentageMax, fireSyntheticChange]);

    const handlePercentageKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const current = parseInt(value?.toString() ?? '', 10);
            const base = isNaN(current) ? (percentageMin ?? 0) : current;
            const step = e.key === 'ArrowUp' ? 1 : -1;
            const next = Math.min(percentageMax, Math.max(percentageMin ?? 0, base + step));
            fireSyntheticChange(next.toString());
        }
    }, [value, percentageMin, percentageMax, fireSyntheticChange]);

    const handlePercentageBlur = useCallback((e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
        const raw = value?.toString() ?? '';
        const num = parseInt(raw, 10);
        const min = percentageMin ?? 0;
        if (raw === '' || isNaN(num) || num < min || num > percentageMax) {
            // Revert to last valid value
            fireSyntheticChange(lastValidPercentage.current);
        }
        if (onBlur) onBlur(e);
        blur();
    }, [value, percentageMin, percentageMax, fireSyntheticChange, onBlur]);

    // Helper function to check if input has a value
    const hasValue = value !== undefined && value !== null && value.toString().length > 0;

    // Handler to clear the input
    const handleClearInput = () => {
        // Create a synthetic event to trigger onChange with empty value
        if (onChange) {
            const syntheticEvent = {
                target: { value: '' },
                currentTarget: { value: '' }
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
        }
    };

    // Props that overwrite native element props.
    const elementProps = {
        id,
        value,
        onChange,
        style: {
            ...style,
            textAlign: textAlign
        },
        onFocus: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
            if (onFocus) onFocus(e);
            focus();
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
            if (onBlur) onBlur(e);
            blur();
        }
    }

    const getInput = () => {
        switch (inputType) {
            case 'numberFormat':
                return <>
                    <NumericFormat
                        {...elementProps}
                        {...(props as NumericFormatProps)}
                    />
                    {currency &&
                        <span className={cx('currency', currency)}>
                            {getCurrencySymbol(currency)}
                        </span>
                    }
                </>
            case 'input':
                return <>
                    <input
                        {...elementProps}
                        {...(props as InputProps)}
                    />
                    {/* Show search icon only when clearInput is enabled and there's no value, or when clearInput is disabled */}
                    {withSearchGlassIcon && (!clearInput || !hasValue) && (
                        <FontAwesomeIcon className='input-icon search-icon' icon={faSearch} />
                    )}
                    {/* Show clear button only when clearInput is enabled and there's a value */}
                    {clearInput && hasValue && (
                        <FontAwesomeIcon
                            className='input-icon clear-icon'
                            icon={faCircleXmark}
                            onClick={handleClearInput}
                        />
                    )}
                </>
            case 'percentage':
                return <>
                    <input
                        {...elementProps}
                        {...(props as InputProps)}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={handlePercentageChange}
                        onKeyDown={handlePercentageKeyDown}
                        onBlur={handlePercentageBlur}
                    />
                    <span className="input-icon percentage-suffix">%</span>
                </>
            case 'password':
                return <>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        {...elementProps}
                        {...(props as InputProps)}
                    />
                    {
                        (elementProps.value !== '') && <>
                            <FontAwesomeIcon icon={faEyeSlash} className={cx("icon-fa", "eye-icon", { 'visible': !showPassword })} onClick={togglePassword} />
                            <FontAwesomeIcon icon={faEye} className={cx("icon-fa", "eye-icon", { 'visible': showPassword })} onClick={togglePassword} />
                        </>
                    }
                </>
            case 'ssnFormat': return <>
                <Cleave
                    {...elementProps}
                    {...(props as InputProps)}
                    options={{ delimiters: ['-'], blocks: [6, 4], numericOnly: true }}
                />
            </>
            case 'expirationFormat': return <>
                <Cleave
                    {...elementProps}
                    {...(props as InputProps)}
                    options={{ date: true, datePattern: ['m', 'y'] }}
                />
            </>
            case 'creditCardFormat': return <>
                <Cleave
                    {...elementProps}
                    {...(props as InputProps)}
                    options={{ creditCard: true, delimiter: '-' }}
                />
            </>
            case 'cvcFormat': return <>
                <Cleave
                    {...elementProps}
                    {...(props as InputProps)}
                    options={{ blocks: [3], numericOnly: true }}
                />
            </>
            default:
                return <textarea
                    {...elementProps}
                    {...(props as TextAreaProps)}
                />
        }
    }

    return (
        <div className={cx('KCL_input', inputSize, className, {
            'is-focused': isFocused,
            'has-value': hasValue,
            'is-disabled': props.disabled,
            'currency_input': currency,
            'has-error': !!error,
            'full-width': fullWidth
        })}>
            {getInput()}
            {label && (
                <label
                    className="input__label"
                    htmlFor={id}
                >
                    <span className="main-label">{label}</span>
                    <span className="short-label">{shortLabel ?? label}</span>
                </label>
            )}
            {error && (
                <div className="input__error-message">
                    {error}
                </div>
            )}
        </div>
    );
}

export default (Input);